import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, ListTodo, ClipboardList, UserRound, Send } from "lucide-react";

const TRACK_COLORS = { on_track: "#10B981", at_risk: "#F59E0B", jeopardy: "#EF4444" };

function MyTaskRow({ t, onChanged }) {
  const [pct, setPct] = useState(t.percent_complete ?? 0);
  const [busy, setBusy] = useState(false);
  const overdue = t.end_date && t.end_date < new Date().toISOString().slice(0, 10) && (t.percent_complete || 0) < 100;

  const setStatus = async (s) => {
    setBusy(true);
    try {
      await api.patch(`/tasks/${t.id}/status`, { status: s });
      toast.success("Task updated");
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update this task"); }
    finally { setBusy(false); }
  };

  const submitPct = async () => {
    setBusy(true);
    try {
      await api.post(`/tasks/${t.id}/submit-progress`, { percent: Number(pct) || 0 });
      toast.success(`Submitted ${pct}% — your PM will confirm it`);
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit progress"); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border px-3 py-2.5" data-testid={`mywork-task-${t.id}`}>
      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: overdue ? "#EF4444" : TRACK_COLORS[t.track_status] || "#64748b" }} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold truncate">{t.is_milestone ? "◆ " : ""}{t.title}</p>
        <p className="text-[11px] text-muted-foreground">
          {t.phase} · {t.start_date || "—"} → {t.end_date || "—"}
          {overdue && <span className="text-red-500 font-bold"> · overdue</span>}
          {t.owner_submitted && t.proposed_percent != null && !t.pm_confirmed && <span className="text-amber-600 font-semibold"> · submitted {t.proposed_percent}% (awaiting PM)</span>}
        </p>
      </div>
      <select value={t.status || "todo"} onChange={(e) => setStatus(e.target.value)} disabled={busy}
        className="h-7 rounded border border-border bg-background px-1.5 text-xs shrink-0" data-testid={`mywork-status-${t.id}`}>
        <option value="todo">To do</option>
        <option value="in_progress">In progress</option>
        <option value="done">Done</option>
      </select>
      <div className="flex items-center gap-1.5 shrink-0">
        <input type="number" min="0" max="100" value={pct} onChange={(e) => setPct(e.target.value)}
          className="h-7 w-16 rounded border border-border bg-background px-1.5 text-xs text-right" data-testid={`mywork-pct-${t.id}`} />
        <span className="text-xs text-muted-foreground">%</span>
        <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={submitPct}
          disabled={busy || Number(pct) === (t.proposed_percent ?? t.percent_complete ?? 0)} data-testid={`mywork-submit-${t.id}`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Submit
        </Button>
      </div>
    </div>
  );
}

function MyActionRow({ a, onChanged }) {
  const [busy, setBusy] = useState(false);
  const setStatus = async (s) => {
    setBusy(true);
    try {
      await api.patch(`/action-items/${a.id}/status`, { status: s });
      toast.success(s === "done" ? "Action item completed" : "Action item updated");
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update"); }
    finally { setBusy(false); }
  };
  const late = a.due_date && a.due_date < new Date().toISOString().slice(0, 10) && a.status !== "done";
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border px-3 py-2" data-testid={`mywork-action-${a.id}`}>
      <ClipboardList className={`h-3.5 w-3.5 shrink-0 ${late ? "text-red-500" : "text-muted-foreground"}`} />
      {a.display_id && <span className="font-mono text-[10px] font-bold text-primary shrink-0">{a.display_id}</span>}
      <span className="text-sm truncate flex-1">{a.title}</span>
      {a.due_date && <span className={`text-xs shrink-0 ${late ? "text-red-500 font-bold" : "text-muted-foreground"}`}>{a.due_date}</span>}
      <select value={a.status || "open"} onChange={(e) => setStatus(e.target.value)} disabled={busy}
        className="h-7 rounded border border-border bg-background px-1.5 text-xs shrink-0" data-testid={`mywork-action-status-${a.id}`}>
        <option value="open">Open</option>
        <option value="in_progress">In progress</option>
        <option value="done">Done</option>
      </select>
    </div>
  );
}

export default function MyWorkView({ projectId }) {
  const [data, setData] = useState(null);
  const load = () => api.get(`/projects/${projectId}/my-work`)
    .then(({ data }) => setData(data))
    .catch(() => setData({ tasks: [], actions: [], names: [] }));
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const openTasks = data.tasks.filter((t) => t.status !== "done");
  const doneTasks = data.tasks.filter((t) => t.status === "done");
  const openActions = data.actions.filter((a) => a.status !== "done");

  return (
    <div className="space-y-6 max-w-4xl" data-testid="mywork-view">
      <div className="flex items-center gap-2.5">
        <UserRound className="h-5 w-5 text-primary" />
        <div>
          <h3 className="font-display text-lg font-bold">My work</h3>
          <p className="text-xs text-muted-foreground">Everything assigned to you on this project. Update status or submit progress — your PM confirms it in the next true-up.</p>
        </div>
      </div>

      {data.tasks.length === 0 && data.actions.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="mywork-empty">
          <ListTodo className="h-8 w-8 text-primary mx-auto mb-3" />
          <p className="text-muted-foreground">Nothing is assigned to you yet. Tasks appear here when the PM sets you as the assignee or responsible.</p>
        </div>
      ) : (
        <>
          {openTasks.length > 0 && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5"><ListTodo className="h-3.5 w-3.5" /> My tasks ({openTasks.length})</p>
              <div className="space-y-2">{openTasks.map((t) => <MyTaskRow key={t.id} t={t} onChanged={load} />)}</div>
            </div>
          )}
          {openActions.length > 0 && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5"><ClipboardList className="h-3.5 w-3.5" /> My action items ({openActions.length})</p>
              <div className="space-y-2">{openActions.map((a) => <MyActionRow key={a.id} a={a} onChanged={load} />)}</div>
            </div>
          )}
          {doneTasks.length > 0 && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-2">Completed ({doneTasks.length})</p>
              <div className="space-y-1">
                {doneTasks.map((t) => (
                  <p key={t.id} className="text-sm text-muted-foreground line-through px-3" data-testid={`mywork-done-${t.id}`}>{t.title}</p>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
